import type { JurisdictionCatalog, JurisdictionEntry, JurisdictionType } from './types';

/**
 * Synthetic stand-in for GET /api/tax-jurisdictions: the same 56 entries with the same honest Phase 1 values as the contract (section 1). Nothing is
 * Certified or PilotOnly, no official source or approver is recorded, and every entry is pending specialist review. The wire names are the server's.
 */
const STATES: readonly [string, string][] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'],
  ['DE', 'Delaware'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'],
  ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'],
  ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const TERRITORIES: readonly [string, string][] = [
  ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'], ['AS', 'American Samoa'], ['MP', 'Commonwealth of the Northern Mariana Islands'],
];
// The existing draft list of states with no wage income tax (a draft pending specialist approval).
const NO_WAGE_INCOME_TAX = new Set(['AK', 'FL', 'NV', 'NH', 'SD', 'TN', 'TX', 'WA', 'WY']);

function entry(code: string, name: string, type: JurisdictionType): JurisdictionEntry {
  return {
    code,
    name,
    type,
    isActive: true,
    groupLabel: type === 'State' ? 'States' : type === 'District' ? 'District' : 'U.S. Territories and Commonwealths',
    availability: 'CalculationNotYetCertified',
    support: {
      configuration: 'ConfigurationAvailable',
      calculation: 'CalculationNotYetCertified',
      withholding: 'CalculationNotYetCertified',
      unemployment: 'CalculationNotYetCertified',
      filing: 'FilingNotYetAvailable',
      payment: 'PaymentNotYetAvailable',
    },
    noWageIncomeTax: type === 'Territory' ? null : NO_WAGE_INCOME_TAX.has(code),
    approvalStatus: 'PendingSmeReview',
    effectiveFrom: null,
    effectiveTo: null,
    officialSources: [],
    lastVerifiedOn: null,
    approver: null,
  };
}

/** States A to Z by name, then the District, then the territories A to Z by name (the order the server returns). */
export function buildMockJurisdictionItems(): JurisdictionEntry[] {
  const byName = (a: readonly [string, string], b: readonly [string, string]) => a[1].localeCompare(b[1]);
  return [
    ...[...STATES].sort(byName).map(([c, n]) => entry(c, n, 'State')),
    entry('DC', 'District of Columbia', 'District'),
    ...[...TERRITORIES].sort(byName).map(([c, n]) => entry(c, n, 'Territory')),
  ];
}

export function buildMockJurisdictionCatalog(): JurisdictionCatalog {
  return {
    version: '2026-10-26-draft-2',
    status: 'DraftPendingSmeApproval',
    note: 'A jurisdiction appearing in this list does not mean payroll tax calculation is certified for it.',
    items: buildMockJurisdictionItems(),
  };
}

/** A quick lookup by code over the mock catalog, shared by the mock gateways and the mock status computation. */
export const MOCK_JURISDICTIONS: ReadonlyMap<string, JurisdictionEntry> = new Map(buildMockJurisdictionItems().map((e) => [e.code, e]));
